import { useMemo } from "react"
import { useFetchRoadDocumentsQuery } from "../../../../api/rtk/base/slices/documentsApiSlice"
import { useFetchAxisLengthQuery } from "../../../../api/rtk/base/slices/pathsApiSlice"
import { useFetchRoadInfoQuery } from "../../../../api/rtk/base/slices/roadInfoApiSlice"
import { useGetTableValueQuery } from "../../../../api/rtk/base/slices/tablesApiSlice"
import { notNullFilter } from "../../../../features/dataFunctions/dataFunctions"
import { formatObject } from "../../../../features/dataFormatFunctions/formatObject"

function calcRoadPositionAndEnding(LinksInfo: any, AxisLinksInfo: any) {
    const links = LinksInfo?.value ?? [];
    const axisLinks = AxisLinksInfo?.value ?? [];
    const mergedLinks = [...links, ...axisLinks];

    // Находим элемент с минимальным MaintenancePositionKm и MaintenancePositionM
    const minItem = mergedLinks.reduce((min, current) => {
        if (
            current.MaintenancePositionKm < min.MaintenancePositionKm ||
            (
                current.MaintenancePositionKm === min.MaintenancePositionKm &&
                current.MaintenancePositionM < min.MaintenancePositionM
            )
        ) {
            return current;
        }
        return min;
    }, mergedLinks[0]);

    const formattedPositionM = Math.abs(minItem.MaintenancePositionM)
        .toFixed()
        .padStart(3, "0");

    const sign = minItem.MaintenancePositionM >= 0 ? "+" : "-";
    const position = `${minItem.MaintenancePositionKm} ${sign} ${formattedPositionM}`;
    let ending

    return { position, ending }
}

export function useRoadInfoCardData(roadId?: string) {
    const {
        data: roadInfo,
        isFetching: isFetchingRoadInfo,
        isError: isRoadInfoError
    } = useFetchRoadInfoQuery({ roadId }, { skip: !roadId })

    // запрос на получение документов конкретной дороги
    const {
        data: roadDocuments,
        isError: isRoadDocumentsError
    } = useFetchRoadDocumentsQuery({ roadId }, { skip: !roadId })

    const { data: highwayNetworkSegmentsInfo, isFetching: isFetchingHighwayNetworkSegmentsInfo } = useGetTableValueQuery({
        tableValueUrl: "objects/HighwayNetworkSegments",
        top: 0,
        pages: true,
        filterString: `RoadID eq ${roadId}`
    }, { skip: !roadId })

    const { data: roadCategorySegmentsInfo, isFetching: isFetchingRoadCategorySegmentsInfo } = useGetTableValueQuery({
        tableValueUrl: "objects/RoadCategorySegments",
        filterString: `RoadID eq ${roadId}`
    }, { skip: !roadId })

    const { data: roadBedLayerSegmentsInfo, isFetching: isFetchingRoadBedLayerSegmentsInfo } = useGetTableValueQuery({
        tableValueUrl: "objects/RoadBedLayerSegments",
        filterString: `RoadID eq ${roadId}`
    }, { skip: !roadId })

    const { data: roadClimaticZoneSegmentsInfo, isFetching: isFetchingRoadClimaticZoneSegmentsInfo } = useGetTableValueQuery({
        tableValueUrl: "objects/RoadClimaticZoneSegments",
        filterString: `RoadID eq ${roadId}`
    }, { skip: !roadId })

    // const { data: soilMoisteningSegmentsInfo, isFetching: isFetchingSoilMoisteningSegmentsInfo } = useFetchTableValueQuery({
    //     tableValueUrl: "objects/SoilMoisteningSegment",
    //     filterString: `RoadID eq ${roadId}`
    // }, { skip: !roadId })

    const { data: roadLengthInfo, isFetching: isFetchingRoadLengthInfo } = useFetchAxisLengthQuery({ RoadId: roadId })

    const { data: LinksInfo, isFetching: isFetchingLinksInfo, isSuccess: isSuccessLinksInfo } = useGetTableValueQuery({
        tableValueUrl: "address/Links",
        filterStringArray: [`RoadID eq ${roadId}`, `PathType eq 1`, `AxisType eq 1`]
    }, { skip: !roadId })

    const { data: AxisLinksInfo, isFetching: isFetchingAxisLinksInfo, isSuccess: isSuccessAxisLinksInfo } = useGetTableValueQuery({
        tableValueUrl: "address/AxisLinks",
        filterStringArray: [`RoadID eq ${roadId}`, `PathType eq 1`, `AxisType eq 1`]
    }, { skip: !roadId })

    const data = useMemo(() => {

        // Доп. информация
        const isInHighwayNetwork = highwayNetworkSegmentsInfo?.["@xdata.count"] > 0;
        const length = roadLengthInfo?.Length
        const roadCategories = [...new Set(roadCategorySegmentsInfo?.value?.map((roadCategorySegment: any) => roadCategorySegment?.CategoryAbbreviation))].filter(notNullFilter).join(', ')
        const roadBedTypes = [...new Set(roadBedLayerSegmentsInfo?.value?.map((roadBedLayerSegment: any) => roadBedLayerSegment?.PavementStructureString))].filter(notNullFilter).join(',\n')
        const roadClimaticZones = [...new Set(roadClimaticZoneSegmentsInfo?.value?.map((roadClimaticZoneSegment: any) => roadClimaticZoneSegment?.ClimaticZoneString))].filter(notNullFilter).join(', ')
        // const soilMoisteningTypes = [...new Set(soilMoisteningSegmentsInfo?.value?.map((soilMoisteningSegment: any) => soilMoisteningSegment?.SoilMoisteningType))].filter(notNullFilter).join(',')

        let position = null;
        let ending = null;
        if (isSuccessLinksInfo && isSuccessAxisLinksInfo) {
            const roadPositionAndEnding = calcRoadPositionAndEnding(LinksInfo, AxisLinksInfo)
            position = roadPositionAndEnding?.position
            ending = roadPositionAndEnding?.ending
        }

        // Финальный объект
        const roadInfoCardData: Record<string, any> = {
            ...roadInfo,
            "add.IsInHighwayNetwork": {
                value: isInHighwayNetwork,
                isLoading: isFetchingHighwayNetworkSegmentsInfo
            },
            "add.Length": {
                value: length,
                isLoading: isFetchingRoadLengthInfo
            },
            "add.RoadCategories": {
                value: roadCategories,
                isLoading: isFetchingRoadCategorySegmentsInfo
            },
            "add.RoadBedTypes": {
                value: roadBedTypes,
                isLoading: isFetchingRoadBedLayerSegmentsInfo
            },
            "add.RoadClimaticZones": {
                value: roadClimaticZones,
                isLoading: isFetchingRoadClimaticZoneSegmentsInfo
            },
            // "add.SoilMoisteningTypes": soilMoisteningTypes
            "add.MaintenancePosition": {
                value: position,
                isLoading: isFetchingLinksInfo || isFetchingAxisLinksInfo
            }
        };

        // Форматированные данные
        const formattedRoadInfoCardData = formatObject(roadInfoCardData);

        return formattedRoadInfoCardData
    }, [roadInfo, highwayNetworkSegmentsInfo, roadLengthInfo, roadCategorySegmentsInfo, roadBedLayerSegmentsInfo, roadClimaticZoneSegmentsInfo, LinksInfo, AxisLinksInfo,
        isFetchingHighwayNetworkSegmentsInfo, isFetchingRoadLengthInfo, isFetchingRoadCategorySegmentsInfo, isFetchingRoadBedLayerSegmentsInfo, isFetchingRoadClimaticZoneSegmentsInfo,
        isFetchingLinksInfo, isFetchingAxisLinksInfo, isSuccessLinksInfo, isSuccessAxisLinksInfo])

    return { data, isLoadingMainData: isFetchingRoadInfo, documents: roadDocuments }
}